/** Die Felddienstordnung. Auszeichnung wie im Englischen: **fett**, *kursiv*, [[Fachwort]]. */
import type { manual as en } from '../en/manual';
import type { Translated } from '../types';

export const manual: Translated<typeof en> = {
  'fm.kicker': 'Anleitung für die Führung von Operationen',
  'fm.title': 'Felddienstordnung',
  'fm.chapters': 'Abschnitte',
  'fm.ch.command': 'Führung',
  'fm.ch.fog': 'Nebel des Krieges',
  'fm.ch.combat': 'Gefecht',
  'fm.ch.supply': 'Versorgung & Kessel',
  'fm.ch.weather': 'Wetter',
  'fm.ch.economy': 'Wirtschaft',
  'fm.ch.victory': 'Sieg',
  'fm.ch.keys': 'Tastenbelegung',
  'fm.academy.kicker': 'Lieber durch Übung lernen?',
  'fm.academy.title': 'Kriegsakademie',
  'fm.academy.text': 'Vierzehn kurze Lehrstunden an einer ruhigen Front, mit Lehroffizier.',
  'fm.docHead': 'FELDDIENSTORDNUNG F.O. 100/5 (FRICTION)',
  'fm.docClass': 'NUR FÜR DEN DIENSTGEBRAUCH',

  'fm.command.h': 'Führung & Kuriere',
  'fm.command.p':
    'Eine Division wird nie unmittelbar bewegt. Sie **schreiben Befehle** — auf der Karte eingezeichnet —, und diese Befehle müssen vom nächsten Hauptquartier zur Truppe gelangen. Bis sie eintreffen, tut der Verband, was ihm zuletzt befohlen wurde.',
  'fm.command.fig.phone': 'FERNSPRECHER · 1 STD.',
  'fm.command.fig.courier': 'KURIER · ABFANGBAR',
  'fm.command.caption':
    'Im **Befehlsbereich** eines Hauptquartiers (3 Felder) gehen Befehle per Feldfernsprecher und treffen binnen einer Stunde ein. Darüber hinaus reitet ein Kurier das Straßennetz ab — auf der Karte sichtbar und abzufangen. Mit *Feldfunk* erreichen Befehle jeden Ort binnen einer Stunde, doch feindliche Entzifferer können mitlesen.',
  'fm.command.li1':
    '[[Marsch]] bewegt, ohne das Gefecht zu suchen. [[Angriff]] rückt vor und greift an. [[Halten]] bleibt stehen und gräbt sich ein. [[Rückzug]] löst sich vom Feind, auf Kosten des Zusammenhalts.',
  'fm.command.li2':
    '[[Haltungen]] sind stehende Befehle für den Fall, dass Kuriere zu spät kommen — Auftragstaktik: Offensive Verbände greifen schwache Nachbarn an, ausgewogene halten, defensive weichen aus, bevor sie zerschlagen werden.',
  'fm.command.li3':
    '[[X-Zeit]]: Erhalten mehrere Verbände dieselbe Angriffszeit, treten sie gemeinsam an — sofern ihre Kuriere rechtzeitig eintreffen.',
  'fm.command.li4':
    'Gehen alle Hauptquartiere verloren, gehen Befehle langsam von der Hauptstadt aus. Die eigenen schützen, die feindlichen jagen.',

  'fm.fog.h': 'Nebel des Krieges & Aufklärung',
  'fm.fog.p':
    'Verbände sehen zwei Felder weit (Aufklärung vier, von Höhen mehr). Wälder, Städte und Gebirge verbergen ihre Besatzung vor jedem, der nicht angrenzt. Was über den Feind bekannt ist, kennt drei Stufen:',
  'fm.fog.contact': 'Kontakt',
  'fm.fog.identified': 'Erkannt',
  'fm.fog.assessed': 'Aufgeklärt',
  'fm.fog.unmasked': 'Attrappe enttarnt',
  'fm.fog.caption':
    'Ein **Kontakt** ist bloß „etwas dort“. **Erkannt** nennt Art und Bezeichnung — Attrappen gehen noch durch. **Aufgeklärt** (angrenzend) zeigt Stärke und Zusammenhalt und enttarnt Attrappen.',
  'fm.fog.li1':
    'Feinde, die außer Sicht geraten, werden zu Altmeldungen mit dem Alter der letzten Sichtung. Nach vier Tagen sind sie vergessen.',
  'fm.fog.li2':
    'Gebiet außer Sicht zeigt den zuletzt bekannten Besitzer — die Front kann sich verschoben haben, ohne dass wir es wissen.',
  'fm.fog.li3':
    'Gefangene Kuriere und entzifferter Funkverkehr erscheinen als rote Pfeile ABGEFANGEN: der eigene Plan des Feindes.',

  'fm.combat.h': 'Gefecht & Flankierung',
  'fm.combat.fig.defender': 'Verteidiger',
  'fm.combat.caption':
    'Jede weitere Angriffsrichtung bringt **+15 %** (höchstens +45 %). Ein Angriff über einen Fluss kostet 30 %. Artillerie im Umkreis von zwei Feldern greift in jedes eigene Gefecht ein.',
  'fm.combat.p':
    'Ein Gefecht wird Stunde um Stunde ausgetragen: Angriffskraft gegen Abwehr, bestimmt von Gelände, Flüssen, Eingraben, Versorgung, Zusammenhalt, Nacht und Flankierung. Der Unterlegene verliert rasch an **Zusammenhalt** und langsam an Stärke. Ein Verteidiger, dessen Zusammenhalt zerbricht, weicht aus — und findet er keinen Ausweg, ergibt er sich.',
  'fm.combat.col.terrain': 'Gelände',
  'fm.combat.col.move': 'Marsch',
  'fm.combat.col.defence': 'Abwehr',
  'fm.combat.col.notes': 'Bemerkungen',
  'fm.combat.note.plains': 'Panzergelände',
  'fm.combat.note.forest': 'Verbirgt; Panzer −30 %',
  'fm.combat.note.hills': '+1 Sicht',
  'fm.combat.note.mountains': 'Verbirgt; Panzer −50 %',
  'fm.combat.note.marsh': 'Panzer −45 %',
  'fm.combat.note.urban': 'Verbirgt; Panzer −40 %',
  'fm.combat.night': 'Nacht (20–5 Uhr): Sicht −1, Marsch 30 % langsamer, Angriffe −20 %.',

  'fm.supply.h': 'Versorgung & der Kessel',
  'fm.supply.p1':
    'Die Versorgung fließt von Hauptstadt und Städten durch das eigene Gebiet, entlang von Straßen und Bahnen weiter. Unversorgte Verbände greifen schwach an und erholen sich nicht; nach zwei Tagen ohne Nachschub und mit zerbrochenem Zusammenhalt ergeben sie sich.',
  'fm.supply.p2':
    'Wer dem Feind die Verbindungen abschneidet, dem meldet der Fernschreiber den Kessel: *„KESSEL — 3 Divisionen bei Dornheim eingekesselt“*. Den Ring halten. Eigene eingeschlossene Verbände sollten sofort ausbrechen — oder entsetzt werden.',

  'fm.weather.h': 'Wetter & Jahreszeiten',
  'fm.weather.p1':
    'Jeder Feldzug hat seine Jahreszeit. Fronten ziehen von Westen heran, Sommernachmittage brüten Gewitter aus, der Herbstregen macht die Felder grundlos, der Winter lässt Flüsse und Sümpfe hart gefrieren. Der Wetterdienst (*W*) gibt alle sechs Stunden eine 24-Stunden-Vorhersage heraus — für die nächsten Stunden genau, für morgen eine Schätzung.',
  'fm.weather.li1':
    '**Regen**: Marsch 20 % langsamer, Angriffe −10 %, Sicht −1, keine Luftbilder. **Unwetter**: Marsch 40 % langsamer, Angriffe −20 %, Kuriere langsamer.',
  'fm.weather.li2':
    '**Schlamm** (abseits der Straßen): Infanterie 40 % langsamer, Panzer und Lastwagen halbe Fahrt und −20 % im Angriff, Versorgung teurer. Die Straßen bleiben befahrbar — sie halten.',
  'fm.weather.li3':
    '**Nebel** (morgens, auf tiefem, nassem Grund): Sicht −2, Verbände nur angrenzend sichtbar, Angreifer +10 %, Kuriere schlüpfen durch. Am Vorabend vorhergesagt.',
  'fm.weather.li4':
    '**Schnee** hemmt die Bewegung abseits der Straßen und verschärft den Hunger; **Frost** lässt Flüsse (keine Brücke nötig) und Sümpfe (tragen wie Ebene) zufrieren.',
  'fm.weather.p2': 'Den Tag der Offensive wählen: das Unwetter abwarten, aus dem Nebel heraus zuschlagen.',

  'fm.economy.h': 'Wirtschaft & Forschung',
  'fm.economy.li1':
    'Versorgte Städte liefern **Industrie** und **Ersatz**. Bis zu vier Aufträge werden gleichzeitig gefertigt; fertige Divisionen erscheinen in der gewählten Stadt.',
  'fm.economy.li2':
    'Forschungspunkte ergeben sich aus einem Grundwert und 12 % der Industrie. Vier Zweige: Fernmeldewesen, Nachrichtendienst, Kriegführung, Nachschubwesen.',
  'fm.economy.li3':
    'Funk macht die Führung schnell, aber undicht; die Entzifferung liest den feindlichen mit; Schlüsselmaschinen schützen den eigenen; Attrappen binden seine Aufmerksamkeit.',

  'fm.victory.h': 'Sieg',
  'fm.victory.li1': 'Städte bringen Siegpunkte (Kleinstadt 1, Stadt 3, Großstadt 6, Hauptstadt +4).',
  'fm.victory.li2':
    'Ein Bündnis, das den Siegesanteil aller Siegpunkte (meist 60 %) 24 Stunden lang hält, gewinnt den Krieg.',
  'fm.victory.li3':
    'Eine Nation, die ihre Hauptstadt verloren hat und weniger als ein Viertel ihrer anfänglichen Siegpunkte hält, kapituliert.',
  'fm.victory.li4':
    'Mit einer Frist siegt, wer bei ihrem Ablauf die meisten Siegpunkte hält. Das letzte Bündnis im Feld siegt immer.',

  'fm.keys.h': 'Tastenbelegung',
};
